
import { useState, useEffect } from "react";
import { Menu, MenuButton, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../networking/axiosconfig";

export default function EmployeeDropdown({ onEmployeeSelected, onUserNameRetrieved }) {
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [error, setError] = useState("");

    const token = localStorage.getItem("token");
    let role = null;
    let userId = localStorage.getItem("userId");

    if (token) {
        try {
            const decodedToken = jwtDecode(token);
            role = decodedToken.role;
            userId = decodedToken.userId;
        } catch (error) {
            console.error("Error decoding token:", error);
        }
    }

    userId = Number(userId);

    useEffect(() => {
        if (selectedEmployees.length > 0) {
            const selectedNames = employees
                .filter((emp) => selectedEmployees.includes(emp.user_id_PK))
                .map((emp) => `${emp.first_name} ${emp.last_name}`.trim());
            onUserNameRetrieved(selectedNames.join(", "));
        } else {
            onUserNameRetrieved("");
        }
    }, [selectedEmployees, employees]);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                let apiUrl = `/api/users`;
                if (role === 3) apiUrl = `/api/users/${userId}`;

                const response = await axiosInstance.get(apiUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!response.data) throw new Error("Invalid response format");

                let employeeData;
                if (role === 3) {
                    employeeData = response.data.data ? [response.data.data] : [];
                } else {
                    employeeData = response.data.data || [];
                }

                if (employeeData.length === 0) {
                    setError("No employees found");
                } else {
                    setError("");
                    setEmployees(employeeData);
                    setFilteredEmployees(employeeData);
                    setSelectedEmployees(employeeData.map((e) => e.user_id_PK));

                    if ((role === 1 || role === 2 || role === 3) && employeeData.length > 0) {
                        const userFullName = `${employeeData[0].first_name} ${employeeData[0].last_name}`.trim();
                        onUserNameRetrieved(userFullName);
                    }
                }
            } catch (error) {
                console.error("API Error:", error);
                setError("Failed to fetch employees");
            }
        };

        fetchEmployees();
    }, [role, userId, token]);

    useEffect(() => {
        onEmployeeSelected(selectedEmployees);
    }, [selectedEmployees]);

    // ✅ handle checkbox toggle
    const handleCheckboxChange = (employeeId) => {
        setSelectedEmployees((prevSelected) =>
            prevSelected.includes(employeeId)
                ? prevSelected.filter((id) => id !== employeeId)
                : [...prevSelected, employeeId]
        );
    };

    // ✅ select/deselect all
    const toggleSelectAll = () => {
        setSelectedEmployees((prevSelected) =>
            prevSelected.length === employees.length ? [] : employees.map((e) => e.user_id_PK)
        );
    };

    // ✅ search employees by name
    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);

        const filtered = employees.filter((emp) =>
            `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(query)
        );
        setFilteredEmployees(filtered);
    };

    return (
        <Menu as="div" className="relative inline-block text-left">
            <div>
                <MenuButton className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 border hover:bg-gray-50">
                    Employee ({selectedEmployees.length})
                    <ChevronDownIcon aria-hidden="true" className="-mr-1 size-5 text-gray-400" />
                </MenuButton>
            </div>

            <MenuItems className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none max-h-72 overflow-y-auto">
                <div className="py-2 px-2">
                    {/* 🔍 Search bar */}
                    {role !== 3 && (
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearch}
                            placeholder="Search employee..."
                            className="w-full mb-2 px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                    )}

                    {/* ✅ Select/Deselect all (only for admin/manager) */}
                    {role !== 3 && (
                        <div
                            className="flex items-center space-x-2 px-4 py-2 cursor-pointer hover:bg-gray-100"
                            onClick={toggleSelectAll}
                        >
                            <input
                                type="checkbox"
                                checked={selectedEmployees.length === employees.length && employees.length > 0}
                                readOnly
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                            <span className="text-gray-900">
                                {selectedEmployees.length === employees.length ? "Deselect All" : "Select All"}
                            </span>
                        </div>
                    )}

                    {/* 🧑 Employee list */}
                    <div className="max-h-full">
                        {filteredEmployees.length > 0 ? (
                            filteredEmployees.map((employee) => {
                                const isSelected = selectedEmployees.includes(employee.user_id_PK);
                                return (
                                    <div
                                        key={employee.user_id_PK}
                                        onClick={() => handleCheckboxChange(employee.user_id_PK)}
                                        className="flex items-center space-x-2 px-4 py-2 cursor-pointer hover:bg-gray-100"
                                    >
                                        {role !== 3 && (
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={() => handleCheckboxChange(employee.user_id_PK)}
                                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                                            />
                                        )}
                                        <span className="text-gray-900">
                                            {employee.first_name} {employee.last_name}
                                        </span>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="text-gray-500 text-center py-2">No employees found</p>
                        )}
                    </div>
                </div>
            </MenuItems>
        </Menu>
    );
}
